/**
 * The turn flow of the game screen as a statechart (docs/ux.md §3, §16). It owns the UI
 * state — the phase, the squadron being chosen, the selection, settings — and a copy of the
 * game's history. The game itself changes only through the `commit` actor: the server in the
 * app, the rules engine in the browser by default (tests). While a command is out, the game
 * waits for its answer. State and transition comments name the U-… IDs they implement.
 */
import { legalActions, volleyCandidates, type Action, type GameState, type Side } from '@brig/rules-engine';
import { and, assign, cancel, enqueueActions, fromCallback, not, setup } from 'xstate';
import {
  canShootAt,
  committed,
  isEnemyUnit,
  openDraft,
  refreshDraft,
  setShots,
  shotQueue,
  shotsLeft,
  type FireDraft,
} from './fire.ts';
import { hintsFor } from './hints.ts';
import { actingUnits, cycle, stepCell, targetsOf } from './keyboard.ts';
import { current, historyReducer, undoDepth, type History, type HistoryMessage } from './history.ts';
import {
  castActions,
  inPortCell,
  isEmptySummary,
  previousSquadron,
  squadronCandidates,
  turnSummary,
} from './turn.ts';

export interface TurnContext {
  readonly history: History;
  /** Ships chosen to sail; UI state until the wind is cast (U-MUST-01). */
  readonly squadron: readonly string[];
  readonly selected: string | null;
  /** Ask before the first cast of the wind (U-MUST-04, §14). */
  readonly confirmCast: boolean;
  /** Full animations, or the reduced-motion variants (U-WIND-01). */
  readonly animate: boolean;
  /** The fire panel's target and shooters, while it is open (U-FIRE-*). */
  readonly fire: FireDraft | null;
  /** Shots still to fire one by one (U-FIRE-07), shooter ids in order. */
  readonly queue: readonly string[];
  /** The keyboard's cell cursor while choosing where to sail (U-KEY-01, §12). */
  readonly cursor: string | null;
  /**
   * The player chose "Move within port" for the selected ship (U-CTX-02): only then do its
   * port berths light up, since such a move uses up the ship's move for the turn (R-PORT-03).
   */
  readonly portMove: boolean;
  /** Perspective, or the flat chart for precise picking (U-VIEW-04). */
  readonly view: View;
  /** The opponent's last turn being replayed (U-LOG-05). */
  readonly replay: Replay | null;
  /** A command sent and not answered yet: no other change to the game meanwhile. */
  readonly pending: Pending | null;
  /**
   * The side played elsewhere, or null when people play both at this screen: by the computer,
   * whose turns come back with the answer to ending one's own and are replayed at once, or by
   * the other player online, whose moves come in as they make them (REMOTE). No handover
   * curtain either way.
   */
  readonly remote: Side | null;
  /** How far into the history the other side's turns have been shown. */
  readonly watched: number;
  /** The phase a replay was started from mid-turn: back to it afterwards, the squadron kept. */
  readonly resume: 'muster' | 'battle' | null;
}

/** A change to the game, sent to whoever keeps it. */
export type Command =
  | { readonly type: 'act'; readonly actions: readonly Action[] }
  | { readonly type: 'undo' }
  | { readonly type: 'rewind' }
  | { readonly type: 'recolour'; readonly colours: History['colours'] };

/** What the turn does once a command is answered. */
export interface Pending {
  readonly then: 'cast' | 'shot' | 'move' | 'apply' | 'end' | 'undo' | 'rewind' | 'recolour';
  /** The ship to select again after an undo (U-UNDO-02). */
  readonly reselect?: string | null;
}

/** A command to send, with what follows its answer and any UI state that changes at once. */
interface Submission {
  readonly command: Command;
  readonly pending: Pending;
  readonly extra?: Partial<TurnContext>;
}

export interface CommitInput {
  readonly history: History;
  readonly command: Command;
}

/** The `commit` actor's one answer: the history with the command applied, or with its error. */
export interface Committed {
  readonly type: 'COMMITTED';
  readonly history: History;
}

/** The game kept in the browser by the rules engine: what the tests play against. */
export function commitLocally(history: History, command: Command): History {
  switch (command.type) {
    case 'act':
      return historyReducer(history, { type: 'applyAll', actions: command.actions });
    case 'undo':
      return historyReducer(history, { type: 'undo' });
    case 'rewind':
      return historyReducer(history, { type: 'rewind' });
    case 'recolour':
      return historyReducer(history, { type: 'recolour', colours: command.colours });
  }
}

/** Answers at once, so a machine on the local engine moves on within the same `send`. */
export const localCommit = fromCallback<{ type: 'NONE' }, CommitInput>(({ input, sendBack }) => {
  sendBack({ type: 'COMMITTED', history: commitLocally(input.history, input.command) } satisfies Committed);
});

export type View = 'perspective' | 'chart';

/** States `from`…`to` of the history shown one by one; `at` is on screen. */
export interface Replay {
  readonly from: number;
  readonly to: number;
  readonly at: number;
  readonly speed: 1 | 2;
}

export interface TurnInput {
  readonly history: History;
  readonly confirmCast?: boolean;
  readonly animate?: boolean;
  readonly view?: View;
  readonly remote?: Side | null;
}

export type TurnEvent =
  // Handover (U-TURN-02)
  | { readonly type: 'START' }
  // Muster (U-MUST-*)
  | { readonly type: 'TOGGLE'; readonly id: string }
  | { readonly type: 'SQUADRON_ALL' }
  | { readonly type: 'SQUADRON_AWAY_FROM_PORT' }
  | { readonly type: 'SQUADRON_CLEAR' }
  | { readonly type: 'CAST' }
  | { readonly type: 'CONFIRM_CAST'; readonly dontAskAgain: boolean }
  | { readonly type: 'CANCEL' }
  | { readonly type: 'SKIP_SAILING' }
  // Wind (U-WIND-05)
  | { readonly type: 'SKIP_ANIM' }
  // Battle (U-MOVE-*, U-MUST-06)
  | { readonly type: 'RAISE_SAILS' }
  | { readonly type: 'END_TURN' }
  | { readonly type: 'CONFIRM_END' }
  // Fire panel (U-FIRE-*)
  | { readonly type: 'AIM'; readonly target: string; readonly from?: string | null }
  | { readonly type: 'SET_SHOTS'; readonly id: string; readonly shots: number }
  | { readonly type: 'VOLLEY' }
  | { readonly type: 'SHOOT' }
  | { readonly type: 'FIRE_SEQUENCE' }
  | { readonly type: 'FIRE_TICK' }
  | { readonly type: 'STOP_FIRE' }
  | { readonly type: 'CLOSE_FIRE' }
  // Keyboard (§12)
  | { readonly type: 'NEXT_UNIT'; readonly dir: 1 | -1 }
  | { readonly type: 'CURSOR'; readonly dx: number; readonly dy: number }
  | { readonly type: 'CONFIRM_CURSOR' }
  | { readonly type: 'CURSOR_OFF' }
  | { readonly type: 'PORT_MOVE' }
  | { readonly type: 'NEXT_TARGET' }
  | { readonly type: 'AIM_ALL' }
  // View and replay (§10.5, §11.3)
  | { readonly type: 'TOGGLE_VIEW' }
  | { readonly type: 'WATCH' }
  | { readonly type: 'REPLAY_PAUSE' }
  | { readonly type: 'REPLAY_PLAY' }
  | { readonly type: 'REPLAY_SPEED'; readonly speed: 1 | 2 }
  | { readonly type: 'REPLAY_SKIP' }
  // Anywhere in a turn
  | { readonly type: 'TAP_UNIT'; readonly id: string }
  | { readonly type: 'TAP_CELL'; readonly cell: string }
  | { readonly type: 'SELECT'; readonly id: string | null }
  | { readonly type: 'APPLY'; readonly action: Action }
  | Committed
  | { readonly type: 'UNDO' }
  | { readonly type: 'REWIND' }
  | { readonly type: 'DISMISS_ERROR' }
  | { readonly type: 'RESET'; readonly history: History }
  /** The game as it now stands, changed by the other player online. */
  | { readonly type: 'REMOTE'; readonly history: History }
  | { readonly type: 'SET_CONFIRM_CAST'; readonly value: boolean }
  /** Presentation only: the players' colours; the game itself is unchanged. */
  | { readonly type: 'RECOLOUR'; readonly colours: History['colours'] }
  /** A file or setup that could not be loaded; the game goes on. */
  | { readonly type: 'FAIL'; readonly message: string };

/** Wind animation length: spinner and arrow, or a short fade with reduced motion. */
export const WIND_MS = { full: 1800, reduced: 400 } as const;

/** Pause between the steps of a replay at 1×, with full or reduced motion (U-LOG-05). */
export const REPLAY_MS = { full: 900, reduced: 400 } as const;

/**
 * The opponent's last turn as a range of states: from the start of their turn to the start
 * of this one (their end-of-turn effects included). Null before anyone has ended a turn.
 */
export function lastTurnRange(history: History): { from: number; to: number } | null {
  const ends = history.actions.flatMap((a, i) => (a.type === 'endTurn' ? [i] : []));
  const end = ends.at(-1);
  if (end === undefined) return null;
  const start = (ends.at(-2) ?? -1) + 1;
  return { from: start, to: end + 1 };
}

/** Pause between shots fired one by one, so each result can be seen (U-FIRE-07). */
export const SHOT_MS = { full: 600, reduced: 150 } as const;

const state = (context: TurnContext): GameState => current(context.history);

function reduce(context: TurnContext, message: HistoryMessage): Pick<TurnContext, 'history'> {
  return { history: historyReducer(context.history, message) };
}

const declarable = (context: TurnContext) => new Set(squadronCandidates(state(context)));

/** The squadron, without ships that can no longer sail (sunk, fired in port, moved…). */
export function liveSquadron(context: TurnContext): string[] {
  const ok = declarable(context);
  return context.squadron.filter((id) => ok.has(id));
}

function isOwnShip(context: TurnContext, id: string): boolean {
  const s = state(context);
  return s.ships.some((x) => x.id === id && x.side === s.turn.side && !x.sunk);
}

/** The ship an action was about, to reselect it after undo (U-UNDO-02). */
function actionShip(action: Action | undefined): string | null {
  if (!action) return null;
  if ('shipId' in action) return action.shipId;
  if ('fortId' in action) return action.fortId;
  return null;
}

type Args = { readonly context: TurnContext; readonly event: TurnEvent };

const act = (pending: Pending, ...actions: Action[]): Submission => ({
  command: { type: 'act', actions },
  pending,
});

/** U-MUST-04: declare and spin, all or nothing. */
const cast = ({ context }: Args): Submission => ({
  command: { type: 'act', actions: castActions(state(context), liveSquadron(context)) },
  pending: { then: 'cast' },
});

const endTurn = ({ context }: Args): Submission =>
  act({ then: 'end' }, { type: 'endTurn', side: state(context).turn.side });

const apply = ({ event }: Args): Submission | null =>
  event.type === 'APPLY' ? act({ then: 'apply' }, event.action) : null;

/** U-FIRE-06: one spin for every committed shot. */
const volley = ({ context }: Args): Submission | null =>
  context.fire
    ? act(
        { then: 'shot' },
        {
          type: 'volley',
          side: state(context).turn.side,
          shooters: committed(context.fire),
          targetId: context.fire.target,
        },
      )
    : null;

/** One shot from the first checked shooter (a single shooter can't volley). */
const shoot = ({ context }: Args): Submission | null => {
  const shooter = context.fire ? committed(context.fire)[0] : undefined;
  return shooter ? fireOne(context, shooter.id) : null;
};

/** U-FIRE-07: the next shot of the sequence; the answer decides whether another follows. */
const fireNext = ({ context }: Args): Submission | null => {
  const [next, ...rest] = context.queue;
  if (next === undefined || !context.fire) return null;
  const shot = fireOne(context, next);
  return shot && { ...shot, extra: { queue: rest } };
};

/** U-MOVE-03: a tap on a highlighted cell moves the selected ship there. */
const moveTo = ({ context, event }: Args): Submission | null => {
  if (event.type !== 'TAP_CELL') return null;
  const action = moveAction(context, event.cell);
  return action ? act({ then: 'move' }, action) : null;
};

/** U-UNDO-01/02: back to the last barrier, one step at a time; the ship is selected again. */
const undo = ({ context }: Args): Submission | null =>
  undoDepth(context.history) === 0
    ? null
    : {
        command: { type: 'undo' },
        pending: { then: 'undo', reselect: actionShip(context.history.actions.at(-1)) },
      };

/** U-UNDO-05: the dev tool, back over anything. */
const rewind = ({ context }: Args): Submission | null =>
  context.history.actions.length === 0 ? null : { command: { type: 'rewind' }, pending: { then: 'rewind' } };

const recolour = ({ event }: Args): Submission | null =>
  event.type === 'RECOLOUR'
    ? { command: { type: 'recolour', colours: event.colours }, pending: { then: 'recolour' } }
    : null;

/**
 * The fire panel's and the keyboard's events, the same in muster and in battle (R-TURN-02:
 * fire at any time).
 */
const FIRE_EVENTS = {
  AIM: { actions: 'aim' },
  SET_SHOTS: { actions: 'setShots' },
  VOLLEY: { guard: 'volleyReady', actions: { type: 'submit', params: volley } },
  SHOOT: { guard: 'canShoot', actions: { type: 'submit', params: shoot } },
  FIRE_SEQUENCE: { guard: 'canShoot', actions: ['startSequence', { type: 'submit', params: fireNext }] },
  FIRE_TICK: { actions: { type: 'submit', params: fireNext } },
  STOP_FIRE: { actions: 'stopFire' },
  CLOSE_FIRE: { actions: 'closeFire' },
  NEXT_UNIT: { actions: ['nextUnit', 'portMoveOff'] },
  PORT_MOVE: { guard: 'hasPortReach', actions: 'togglePortMove' },
  CURSOR: { guard: 'hasReach', actions: 'moveCursor' },
  CONFIRM_CURSOR: { guard: 'cursorOut', actions: 'confirmCursor' },
  CURSOR_OFF: { actions: 'cursorOff' },
  NEXT_TARGET: { actions: 'nextTarget' },
  AIM_ALL: { actions: 'aimAll' },
} as const;

export const turnMachine = setup({
  types: {
    context: {} as TurnContext,
    events: {} as TurnEvent,
    input: {} as TurnInput,
  },
  actors: { commit: localCommit },
  delays: {
    wind: ({ context }) => (context.animate ? WIND_MS.full : WIND_MS.reduced),
    shot: ({ context }) => (context.animate ? SHOT_MS.full : SHOT_MS.reduced),
    replayStep: ({ context }) =>
      (context.animate ? REPLAY_MS.full : REPLAY_MS.reduced) / (context.replay?.speed ?? 1),
  },
  guards: {
    gameOver: ({ context }) => state(context).status.kind !== 'playing',
    /** A turn is about to begin: the first of the game, or one after the other side's. */
    handoverDue: ({ context }) => {
      if (context.remote) return false;
      const actions = context.history.actions;
      return actions.length === 0 || actions.at(-1)?.type === 'endTurn';
    },
    /** The other side has just played a turn the player has not seen yet. */
    remoteMoved: ({ context }) => {
      const last = context.history.actions.at(-1);
      return (
        last?.type === 'endTurn' &&
        last.side === context.remote &&
        context.history.actions.length > context.watched &&
        lastTurnRange(context.history) !== null
      );
    },
    /** A change from elsewhere that hands the turn on, or ends the game. */
    turnPassed: ({ context, event }) => {
      if (event.type !== 'REMOTE') return false;
      const before = state(context);
      const after = current(event.history);
      return after.turn.number !== before.turn.number || after.status.kind !== before.status.kind;
    },
    /** The other player online is to move: the screen only watches. */
    theirTurn: ({ context }) =>
      context.remote !== null &&
      state(context).status.kind === 'playing' &&
      state(context).turn.side === context.remote,
    windCast: ({ context }) => state(context).turn.phase === 'spun',
    /** The cast came back refused (the error is in the history). */
    castRefused: ({ context }) => context.pending === null && state(context).turn.phase !== 'spun',
    settled: ({ context }) => context.pending === null,
    /** The answer starts the next turn, or puts the clock back: pick the phase afresh. */
    reroute: ({ context }) => context.pending?.then === 'end' || context.pending?.then === 'rewind',
    squadronEmpty: ({ context }) => liveSquadron(context).length === 0,
    askBeforeCast: ({ context }) => context.confirmCast,
    /** The group may still be declared this turn (R-TURN-02, U-MUST-06). */
    canRaiseSails: ({ context }) => state(context).turn.phase === 'open',
    nothingLeft: ({ context }) => isEmptySummary(turnSummary(state(context))),
    /** R-VOLLEY-01: enough shooters checked for one combined spin. */
    volleyReady: ({ context }) =>
      context.fire !== null && committed(context.fire).length >= state(context).ruleset.volleyMinShooters,
    canShoot: ({ context }) => context.fire !== null && committed(context.fire).length > 0,
    /** Shots are under way, or a command is out: the turn can't move on yet. */
    firing: ({ context }) => context.queue.length > 0 || context.pending !== null,
    canWatch: ({ context }) => lastTurnRange(context.history) !== null,
    /** U-LOG-05 mid-turn: the replay may start once nothing is under way. */
    canWatchNow: ({ context }) =>
      lastTurnRange(context.history) !== null && context.queue.length === 0 && context.pending === null,
    backToMuster: ({ context }) => context.resume === 'muster',
    backToBattle: ({ context }) => context.resume === 'battle',
    replayDone: ({ context }) => !context.replay || context.replay.at >= context.replay.to,
    /** The selected ship has somewhere to go: the cursor can pick it (U-KEY-03). */
    hasReach: ({ context }) => {
      const sel = context.selected;
      if (!sel) return false;
      const s = state(context);
      const h = hintsFor(s, legalActions(s, s.turn.side), sel);
      return h.moveCells.size + (context.portMove ? h.portCells.size : 0) > 0;
    },
    hasPortReach: ({ context }) => {
      const sel = context.selected;
      if (!sel) return false;
      const s = state(context);
      return hintsFor(s, legalActions(s, s.turn.side), sel).portCells.size > 0;
    },
    cursorOut: ({ context }) => context.cursor !== null,
  },
  actions: {
    prefillSquadron: assign({
      squadron: ({ context }) => previousSquadron(state(context), context.history.actions),
      selected: null,
    }),
    toggle: assign({
      squadron: ({ context, event }) => {
        if (event.type !== 'TOGGLE') return context.squadron;
        const live = liveSquadron(context);
        if (live.includes(event.id)) return live.filter((id) => id !== event.id);
        return declarable(context).has(event.id) ? [...live, event.id] : live;
      },
    }),
    squadronAll: assign({ squadron: ({ context }) => squadronCandidates(state(context)) }),
    squadronAwayFromPort: assign({
      squadron: ({ context }) =>
        squadronCandidates(state(context)).filter((id) => !inPortCell(state(context), id)),
    }),
    squadronClear: assign({ squadron: [] }),
    /** Sends a command to whoever keeps the game, unless one is already out. */
    submit: enqueueActions(({ context, enqueue }, params: Submission | null) => {
      if (!params || context.pending) return;
      enqueue.assign({ ...params.extra, pending: params.pending });
      enqueue.spawnChild('commit', {
        id: 'commit',
        input: { history: context.history, command: params.command },
      });
    }),
    /** The answer: the game as it now stands, and the turn picks up where it waited. */
    committed: enqueueActions(({ context, event, enqueue }) => {
      if (event.type !== 'COMMITTED') return;
      enqueue.stopChild('commit');
      const { history } = event;
      const ok = history.error === null;
      // Moves, shots and undo change who can reach the target: keep the panel honest.
      const fire = context.fire ? refreshDraft(current(history), context.fire) : null;
      const then = context.pending?.then;
      // U-FIRE-08: the sequence goes on while the target floats and the shot went off.
      const queue = then === 'shot' && !(fire !== null && ok) ? [] : context.queue;
      const selected =
        then === 'undo' && ok ? (context.pending?.reselect ?? context.selected) : context.selected;
      enqueue.assign({ history, pending: null, fire, queue, selected });
      if (then === 'shot' && queue.length > 0)
        enqueue.raise({ type: 'FIRE_TICK' }, { id: 'shot', delay: 'shot' });
    }),
    dontAskAgain: assign({
      confirmCast: ({ context, event }) =>
        event.type === 'CONFIRM_CAST' && event.dontAskAgain ? false : context.confirmCast,
    }),
    /**
     * U-MUST-01: in muster a tap on an own ship that may sail toggles it in the squadron, and
     * nothing else: no card opens (a long press shows it). Other units are selected as usual.
     */
    tapUnitMuster: assign(({ context, event }) => {
      if (event.type !== 'TAP_UNIT') return {};
      const shared = tapShared(context, event.id);
      if (shared) return shared;
      if (isOwnShip(context, event.id) && declarable(context).has(event.id)) {
        const live = liveSquadron(context);
        const squadron = live.includes(event.id) ? live.filter((id) => id !== event.id) : [...live, event.id];
        return { squadron, selected: null, cursor: null };
      }
      return { selected: context.selected === event.id ? null : event.id };
    }),
    /** U-MOVE-02, U-FIRE-01/02 */
    tapUnit: assign(({ context, event }) => {
      if (event.type !== 'TAP_UNIT') return {};
      return tapShared(context, event.id) ?? { selected: context.selected === event.id ? null : event.id };
    }),
    aim: assign(({ context, event }) => {
      if (event.type !== 'AIM') return {};
      const s = state(context);
      if (!isEnemyUnit(s, event.target) || volleyCandidates(s, event.target).length === 0) return {};
      return { fire: openDraft(s, event.target, event.from ?? null), queue: [] };
    }),
    setShots: assign(({ context, event }) =>
      event.type === 'SET_SHOTS' && context.fire
        ? { fire: setShots(state(context), context.fire, event.id, event.shots) }
        : {},
    ),
    startSequence: assign({
      queue: ({ context }) => (context.fire && !context.pending ? shotQueue(context.fire) : context.queue),
    }),
    stopFire: enqueueActions(({ enqueue }) => {
      enqueue(cancel('shot'));
      enqueue.assign({ queue: [] });
    }),
    closeFire: enqueueActions(({ enqueue }) => {
      enqueue(cancel('shot'));
      enqueue.assign({ fire: null, queue: [] });
    }),
    /** U-MOVE-03: a tap elsewhere than a highlighted cell deselects. */
    tapCellMiss: assign(({ context, event }) =>
      event.type === 'TAP_CELL' && !moveAction(context, event.cell) ? { selected: null } : {},
    ),
    confirmCursor: enqueueActions(({ context, enqueue }) => {
      if (context.cursor) enqueue.raise({ type: 'TAP_CELL', cell: context.cursor });
    }),
    select: assign({
      selected: ({ context, event }) => (event.type === 'SELECT' ? event.id : context.selected),
      cursor: null,
    }),
    deselect: assign({ selected: null, cursor: null }),
    inspect: assign({
      selected: ({ context, event }) =>
        event.type !== 'TAP_UNIT' || context.selected === event.id ? null : event.id,
    }),
    /** U-KEY-02 */
    nextUnit: assign(({ context, event }) => {
      if (event.type !== 'NEXT_UNIT') return {};
      const mustering = !state(context).turn.spin;
      const next = cycle(actingUnits(state(context), mustering), context.selected, event.dir);
      return next ? { selected: next, cursor: null } : {};
    }),
    moveCursor: assign(({ context, event }) => {
      if (event.type !== 'CURSOR' || !context.selected) return {};
      const s = state(context);
      const from = context.cursor ?? s.ships.find((x) => x.id === context.selected)?.cell ?? null;
      return from ? { cursor: stepCell(s, from, event.dx, event.dy) } : {};
    }),
    cursorOff: assign({ cursor: null }),
    togglePortMove: assign({ portMove: ({ context }) => !context.portMove, cursor: null }),
    portMoveOff: assign({ portMove: false }),
    /** U-FIRE-03: F aims the selected shooter at its next target. */
    nextTarget: assign(({ context }) => {
      const s = state(context);
      const sel = context.selected;
      if (!sel || shotsLeft(s, sel) === 0) return {};
      const next = cycle(targetsOf(s, sel), context.fire?.target ?? null, 1);
      return next ? { fire: openDraft(s, next, sel), queue: [] } : {};
    }),
    /** V: everyone who reaches the target aims at it. */
    aimAll: assign(({ context }) => {
      const s = state(context);
      const target = context.fire?.target ?? context.selected;
      if (!target || !isEnemyUnit(s, target) || volleyCandidates(s, target).length === 0) return {};
      return { fire: openDraft(s, target), queue: [] };
    }),
    toggleView: assign({ view: ({ context }) => (context.view === 'chart' ? 'perspective' : 'chart') }),
    startReplay: assign(({ context }) => {
      const range = lastTurnRange(context.history);
      return range
        ? { replay: { ...range, at: range.from, speed: 1 as const }, selected: null, cursor: null }
        : {};
    }),
    stepReplay: assign({
      replay: ({ context }) =>
        context.replay ? { ...context.replay, at: Math.min(context.replay.to, context.replay.at + 1) } : null,
    }),
    setReplaySpeed: assign({
      replay: ({ context, event }) =>
        context.replay && event.type === 'REPLAY_SPEED'
          ? { ...context.replay, speed: event.speed }
          : context.replay,
    }),
    endReplay: assign({ replay: null }),
    resumeMuster: assign({ resume: 'muster' as const }),
    resumeBattle: assign({ resume: 'battle' as const }),
    forgetResume: assign({ resume: null }),
    markWatched: assign({ watched: ({ context }) => context.history.actions.length }),
    dismissError: assign(({ context }) => reduce(context, { type: 'dismissError' })),
    reset: enqueueActions(({ context, event, enqueue }) => {
      if (event.type !== 'RESET') return;
      if (context.pending) enqueue.stopChild('commit');
      enqueue.assign({
        history: event.history,
        squadron: [],
        selected: null,
        fire: null,
        queue: [],
        pending: null,
      });
    }),
    /** The other player's change, seen as it happens; the open panel stays honest. */
    remote: assign(({ context, event }) => {
      if (event.type !== 'REMOTE') return {};
      const fire = context.fire ? refreshDraft(current(event.history), context.fire) : null;
      return { history: event.history, fire, watched: event.history.actions.length };
    }),
    fail: assign(({ context, event }) =>
      event.type === 'FAIL' ? reduce(context, { type: 'fail', message: event.message }) : {},
    ),
    setConfirmCast: assign({
      confirmCast: ({ context, event }) =>
        event.type === 'SET_CONFIRM_CAST' ? event.value : context.confirmCast,
    }),
  },
}).createMachine({
  id: 'turn',
  context: ({ input }) => ({
    history: input.history,
    squadron: [],
    selected: null,
    confirmCast: input.confirmCast ?? true,
    animate: input.animate ?? true,
    fire: null,
    queue: [],
    cursor: null,
    portMove: false,
    view: input.view ?? 'perspective',
    replay: null,
    pending: null,
    remote: input.remote ?? null,
    watched: input.history.actions.length,
    resume: null,
  }),
  initial: 'route',
  on: {
    SELECT: { actions: ['select', 'portMoveOff'] },
    // Any engine action from a panel; resign and draw are legal at any time (R-TURN-08).
    APPLY: { actions: { type: 'submit', params: apply } },
    COMMITTED: [{ guard: 'reroute', target: '.route', actions: 'committed' }, { actions: 'committed' }],
    DISMISS_ERROR: { actions: 'dismissError' },
    RESET: { target: '.route', actions: 'reset' },
    // While a command is out its answer (or the reload after a conflict) brings the change. A
    // change that passes the turn (made in another tab, say) picks the phase afresh.
    REMOTE: [
      { guard: and(['settled', 'turnPassed']), target: '.route', actions: 'remote' },
      { guard: 'settled', actions: 'remote' },
    ],
    REWIND: { actions: { type: 'submit', params: rewind } },
    SET_CONFIRM_CAST: { actions: 'setConfirmCast' },
    RECOLOUR: { actions: { type: 'submit', params: recolour } },
    FAIL: { actions: 'fail' },
    TOGGLE_VIEW: { actions: 'toggleView' },
  },
  states: {
    /** Picks the phase from the game state: on load, after a reset and after each turn. */
    route: {
      always: [
        { guard: 'gameOver', target: 'over', actions: 'forgetResume' },
        { guard: 'remoteMoved', target: 'replay', actions: 'forgetResume' },
        { guard: 'theirTurn', target: 'waiting', actions: 'forgetResume' },
        { guard: 'backToMuster', target: 'muster', actions: 'forgetResume' },
        { guard: 'backToBattle', target: 'battle', actions: 'forgetResume' },
        { guard: 'handoverDue', target: 'handover' },
        { guard: 'windCast', target: 'battle' },
        { target: 'muster', actions: 'prefillSquadron' },
      ],
    },
    /** U-TURN-01..02: the curtain before each turn; on the first it announces the lot. */
    handover: {
      entry: 'deselect',
      always: { guard: 'gameOver', target: 'over' },
      on: {
        START: { target: 'muster', actions: 'prefillSquadron' },
        WATCH: { guard: 'canWatch', target: 'replay' },
      },
    },
    /** U-LOG-05: the opponent's last turn, step by step; no game input meanwhile. */
    replay: {
      entry: 'startReplay',
      exit: ['endReplay', 'markWatched'],
      initial: 'playing',
      on: {
        REPLAY_SKIP: '#turn.route',
        REPLAY_SPEED: { actions: 'setReplaySpeed' },
      },
      states: {
        playing: {
          after: {
            replayStep: [
              { guard: 'replayDone', target: '#turn.route' },
              { target: 'playing', reenter: true, actions: 'stepReplay' },
            ],
          },
          on: { REPLAY_PAUSE: 'paused', SKIP_ANIM: '#turn.route' },
        },
        paused: { on: { REPLAY_PLAY: 'playing' } },
      },
    },
    /** U-MUST-*: choose who sails. Firing and in-port moves are allowed too (R-TURN-02). */
    muster: {
      initial: 'choosing',
      always: { guard: 'gameOver', target: 'over' },
      exit: 'closeFire',
      on: {
        ...FIRE_EVENTS,
        TOGGLE: { actions: 'toggle' },
        SQUADRON_ALL: { actions: 'squadronAll' },
        SQUADRON_AWAY_FROM_PORT: { actions: 'squadronAwayFromPort' },
        SQUADRON_CLEAR: { actions: 'squadronClear' },
        TAP_UNIT: { actions: ['tapUnitMuster', 'portMoveOff'] },
        TAP_CELL: {
          actions: [{ type: 'submit', params: moveTo }, 'tapCellMiss', 'cursorOff', 'portMoveOff'],
        },
        UNDO: { actions: { type: 'submit', params: undo } },
        SKIP_SAILING: { target: 'battle' },
      },
      states: {
        choosing: {
          on: {
            WATCH: { guard: 'canWatchNow', target: '#turn.replay', actions: 'resumeMuster' },
            CAST: [
              { guard: 'squadronEmpty' },
              { guard: 'firing' },
              { guard: 'askBeforeCast', target: 'confirming' },
              { target: '#turn.wind', actions: { type: 'submit', params: cast } },
            ],
          },
        },
        /** U-MUST-04: first-use explanation of the point of no return. */
        confirming: {
          on: {
            CONFIRM_CAST: {
              target: '#turn.wind',
              actions: ['dontAskAgain', { type: 'submit', params: cast }],
            },
            CANCEL: { target: 'choosing' },
          },
        },
      },
    },
    /** U-WIND-01..05: the spinner, the arrow, storm drift. No game input. */
    wind: {
      entry: 'deselect',
      initial: 'casting',
      always: [
        { guard: 'gameOver', target: 'over' },
        // The cast was refused (the error is in the history): back to choosing.
        { guard: 'castRefused', target: 'muster' },
      ],
      on: { SKIP_ANIM: { guard: 'settled', target: 'battle' } },
      states: {
        /** The cast is out: the spinner turns until the wind comes back. */
        casting: { always: { guard: 'settled', target: 'blowing' } },
        blowing: { after: { wind: '#turn.battle' } },
      },
    },
    /** U-MOVE-*, firing; U-MUST-06 raising sails after skipping. */
    battle: {
      initial: 'playing',
      always: { guard: 'gameOver', target: 'over' },
      exit: 'closeFire',
      on: {
        ...FIRE_EVENTS,
        TAP_UNIT: { actions: ['tapUnit', 'portMoveOff'] },
        TAP_CELL: {
          actions: [{ type: 'submit', params: moveTo }, 'tapCellMiss', 'cursorOff', 'portMoveOff'],
        },
        UNDO: { actions: { type: 'submit', params: undo } },
      },
      states: {
        playing: {
          on: {
            WATCH: { guard: 'canWatchNow', target: '#turn.replay', actions: 'resumeBattle' },
            RAISE_SAILS: { guard: 'canRaiseSails', target: '#turn.muster' },
            // U-END-01 / U-END-02
            END_TURN: [
              { guard: 'firing' },
              // The answer starts the next turn (COMMITTED reroutes).
              { guard: 'nothingLeft', actions: { type: 'submit', params: endTurn } },
              { target: 'confirmingEnd' },
            ],
          },
        },
        /** U-END-02: the summary of what would go unused. */
        confirmingEnd: {
          on: {
            CONFIRM_END: { target: 'playing', actions: { type: 'submit', params: endTurn } },
            CANCEL: { target: 'playing' },
            SELECT: { target: 'playing', actions: 'select' },
          },
        },
      },
    },
    /** The other player online is making their turn; their moves show as they come in. */
    waiting: {
      entry: 'deselect',
      always: { guard: not('theirTurn'), target: 'route' },
      // A unit's card may be opened to look at it; nothing else answers.
      on: { TAP_UNIT: { actions: 'inspect' } },
    },
    /** U-OVER-01 */
    over: { on: { WATCH: { guard: 'canWatch', target: 'replay' } } },
  },
});

/** A tap on a unit while firing is possible: aim at an enemy, or check a shooter in the panel. */
function tapShared(context: TurnContext, id: string): Partial<TurnContext> | null {
  const s = state(context);
  if (isEnemyUnit(s, id)) {
    // U-FIRE-02: the selected shooter aims at it; U-FIRE-01: everyone who reaches it does.
    if (canShootAt(s, context.selected, id)) return { fire: openDraft(s, id, context.selected), queue: [] };
    if (volleyCandidates(s, id).length > 0) return { fire: openDraft(s, id), selected: id, queue: [] };
    return { fire: null, selected: id };
  }
  const fire = context.fire;
  if (fire && id in fire.shots) {
    // U-FIRE-04: an own shooter tapped while aiming joins or leaves the shot.
    const on = (fire.shots[id] ?? 0) > 0;
    return { fire: setShots(s, fire, id, on ? 0 : shotsLeft(s, id)) };
  }
  return fire ? { fire: null } : null;
}

/** One single shot from `shooterId` at the panel's target. */
function fireOne(context: TurnContext, shooterId: string): Submission | null {
  const fire = context.fire;
  if (!fire) return null;
  return act(
    { then: 'shot' },
    { type: 'fire', side: state(context).turn.side, shooterId, targetId: fire.target },
  );
}

/** The move a tap on `cell` makes for the selected ship, if the cell is lit for it. */
function moveAction(context: TurnContext, cell: string): Action | null {
  const sel = context.selected;
  if (!sel) return null;
  const s = state(context);
  const side = s.turn.side;
  const hints = hintsFor(s, legalActions(s, side), sel);
  if (hints.moveCells.has(cell)) return { type: 'moveShip', side, shipId: sel, to: cell };
  if (context.portMove && hints.portCells.has(cell))
    return { type: 'moveInPort', side, shipId: sel, to: cell };
  return null;
}
